import React from 'react'
import {
  componentLoader,
  ComponentMetadata,
  MetadataJsonContentType,
  pageBlockRegistry,
  PageBlockMetadata,
} from '../registry'
import { mergeMetadataWithJsDocMetadata } from './metadata'

export const registerComponent = (
  packageName: string,
  name: string,
  Component: React.ComponentType<any>,
  metadata?: ComponentMetadata,
) =>
  componentLoader.registerComponent(
    packageName,
    name,
    {
      type: 'react',
      Component,
    },
    metadata,
  )

/**
 * This function registers a component to be able to automatically show documentation from JSDoc comments by hooking it up
 * to the metadata.json file.  It does not automatically appear in docs until the tags @documentComponent are used to
 * show the component with a preview, or @documentProperties is used to just show the properties without the preview.
 *
 * This function should be wrapped in the consuming repo so that the metadata.json doesn't have to be passed in every time
 * it is registered.
 *
 * It can also render an example component using the @example tag on a component.
 */
export const registerComponentWithJsDocs = (
  packageName: string,
  name: string,
  Component: React.ComponentType<any>,
  metadata?: ComponentMetadata & { jsDocTags?: Record<string, any> },
  metadataJsonContent?: MetadataJsonContentType,
): void => {
  const generatedMetadata = mergeMetadataWithJsDocMetadata(
    packageName,
    name,
    metadata,
    metadataJsonContent,
  )
  return registerComponent(
    packageName,
    name,
    Component,
    generatedMetadata as ComponentMetadata,
  )
}

/**
 * Registers a page block — a pre-configured PageComponent tree that can be
 * selected in the page builder as a reusable building block. Users can
 * override individual properties while the base configuration stays dynamic.
 */
export const registerPageBlock = (
  packageName: string,
  name: string,
  config: Record<string, unknown>,
  metadata: PageBlockMetadata,
) => pageBlockRegistry.registerBlock(packageName, name, config, metadata)
